import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import { Warehouse } from '../../stock/models/StockLine';
import getWarehouseOptions from '../../stock/services/getWarehouseOptions';

interface UploadStockTransfersModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (file: File, warehouseId: number) => Promise<void>;
}

export function UploadStockTransfersModal({
  visible,
  onClose,
  onSubmit,
}: UploadStockTransfersModalProps) {
  const intl = useIntl();
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(
    null
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const warehouseOptions = getWarehouseOptions(intl, false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setSelectedFile(file || null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile || !selectedWarehouse) return;

    setIsSubmitting(true);
    try {
      await onSubmit(selectedFile, selectedWarehouse);
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedWarehouse(null);
    setSelectedFile(null);
    onClose();
  };

  const isFormValid = selectedFile && selectedWarehouse;

  return (
    <Modal show={visible} onHide={handleClose} backdrop="static" centered>
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {intl.formatMessage({ id: 'stockTransfers.uploadExcel' })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-5">
            <label className="form-label fw-bold required">
              {intl.formatMessage({ id: 'stockTransfers.warehouse' })}
            </label>
            <Select
              options={warehouseOptions}
              value={warehouseOptions.find(
                (opt) => opt.value === selectedWarehouse
              )}
              onChange={(option) =>
                setSelectedWarehouse((option?.value as Warehouse) || null)
              }
              placeholder={intl.formatMessage({
                id: 'stockTransfers.selectWarehouse',
              })}
              isClearable
            />
          </div>
          <div className="mb-3">
            <label className="form-label fw-bold required">
              {intl.formatMessage({ id: 'stockTransfers.file' })}
            </label>
            <input
              type="file"
              className="form-control"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {intl.formatMessage({ id: 'common.cancel' })}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!isFormValid || isSubmitting}
          >
            {isSubmitting ? (
              <span className="spinner-border spinner-border-sm me-2" />
            ) : (
              <i className="fa fa-upload me-2" />
            )}
            {intl.formatMessage({ id: 'stockTransfers.registerTransfers' })}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
